import {
  Users,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { formatFCFA } from "@cieda/utils";
import type { CustomerSummary } from "./types";
import { SEGMENT_STYLES, SEGMENT_LABELS, PAGE_SIZE } from "./constants";

// ---------------------------------------------------------------------------
// CustomersTable — list view table with loading / error / empty states and
// pagination. Pure presentational: no data fetching, all state comes from
// props.
// ---------------------------------------------------------------------------

interface CustomersTableProps {
  readonly customers: readonly CustomerSummary[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly hasData: boolean;
  readonly totalFromApi: number;
  readonly totalPages: number;
  readonly page: number;
  readonly onPageChange: (page: number) => void;
  readonly onSelectCustomer: (id: string) => void;
}

export function CustomersTable({
  customers,
  isLoading,
  isError,
  hasData,
  totalFromApi,
  totalPages,
  page,
  onPageChange,
  onSelectCustomer,
}: CustomersTableProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
      {/* Loading skeleton */}
      {isLoading && !hasData && (
        <div className="divide-y divide-stone-100">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3">
              <div className="h-8 w-8 rounded-full bg-stone-100" />
              <div className="h-4 w-36 rounded bg-stone-100" />
              <div className="ml-auto h-4 w-20 rounded bg-stone-100" />
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
          <AlertCircle className="h-8 w-8 text-red-300" />
          <p className="text-sm font-medium text-stone-600">Impossible de charger les clients</p>
        </div>
      )}

      {!isLoading && !isError && customers.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
          <Users className="h-10 w-10 text-stone-200" />
          <p className="text-sm font-medium text-stone-500">Aucun client trouvé</p>
          <p className="text-xs text-stone-400">Essayez de modifier vos filtres de recherche.</p>
        </div>
      ) : !isError && customers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Client
                </th>
                <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 sm:table-cell">
                  Téléphone
                </th>
                <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 md:table-cell">
                  Région
                </th>
                <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 md:table-cell">
                  Segment
                </th>
                <th className="hidden px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-stone-500 lg:table-cell">
                  Commandes
                </th>
                <th className="hidden px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500 lg:table-cell">
                  Total dépensé
                </th>
                <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 xl:table-cell">
                  Dernière commande
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="cursor-pointer transition-colors hover:bg-stone-50"
                  onClick={() => onSelectCustomer(customer.id)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-medium text-amber-800">
                        {(customer.full_name || '?')[0]}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{customer.full_name ?? 'Client sans nom'}</p>
                        <p className="text-xs text-stone-400 sm:hidden">{customer.phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-stone-600 sm:table-cell">
                    {customer.phone}
                  </td>
                  <td className="hidden px-4 py-3 text-stone-500 md:table-cell">
                    {customer.region}
                  </td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${SEGMENT_STYLES[customer.segment]}`}>
                      {SEGMENT_LABELS[customer.segment]}
                    </span>
                  </td>
                  <td className="hidden px-4 py-3 text-center tabular-nums text-stone-700 lg:table-cell">
                    {customer.total_orders}
                  </td>
                  <td className="hidden px-4 py-3 text-right tabular-nums font-medium text-stone-800 lg:table-cell">
                    {formatFCFA(customer.total_spent_fcfa)}
                  </td>
                  <td className="hidden px-4 py-3 text-stone-500 xl:table-cell">
                    {customer.last_order_date ?? "—"}
                  </td>
                  <td className="px-2 py-3">
                    <ChevronRight className="h-4 w-4 text-stone-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalFromApi > PAGE_SIZE && (
        <div className="flex items-center justify-between border-t border-stone-100 px-4 py-3">
          <p className="text-xs text-stone-500">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalFromApi)} sur {totalFromApi} clients
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="rounded-md p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const offset = Math.max(0, Math.min(page - 4, totalPages - 7));
              return offset + i + 1;
            }).map((p) => (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`h-8 w-8 rounded-md text-xs font-medium transition-colors ${
                  p === page
                    ? "bg-amber-100 text-amber-800"
                    : "text-stone-600 hover:bg-stone-100"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="rounded-md p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
